"use client";
import axios from "axios";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/i18n/navigation";
import { RefObject, useEffect, useState } from "react";

import { Dayjs } from "dayjs";
import { components, SingleValueProps } from "react-select";
import Dropdown from "../Dropdown";
import "./index.css";

import { COLORS } from "@/app/common/consts/colors";
import { getFilteredParams } from "@/app/common/utils/getFilteredParams";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import { useToast } from "@/app/hooks/toast/useToast";
import { Place } from "@/app/types/common";
import {
  API_DATE_FORMAT,
  MAX_TRAVELER_COUNT,
  SEARCH_PARAMS,
} from "@/app/utils/constants/constants";
import getDeviceType from "@/app/utils/device/getDeviceType";
import { getDatesDiff, redirect } from "@/app/utils/services/helper/helper";
import {
  getLastSearchRequestFromLocalStorage,
  getPartnerCode,
  setLastSearchRequestToLocalStorage,
} from "@/app/utils/services/service/localStorageService";
import { Destination, GeoLocation, OptionType, SelectOption } from "@/types";
import { isEmpty } from "lodash";
import {
  LoaderCircle,
  Minus,
  Plus,
  Search as SearchIcon,
  Users,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { CustomDatePicker } from "../DatePicker";
import { useDatePickerContext } from "../DatePicker/DatePickerContext";
import CustomSelect from "../Dropdown/CustomDropDrown";
import useSetSearchParams from "./useSetSearchParams";

// countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

type Props = {
  searchRef?: RefObject<HTMLDivElement>;
  calendarRef?: RefObject<HTMLDivElement>;
  isHomePage?: boolean;
  destination: Destination;
  setDestination: (val: Destination) => void;
  isCustomClass?: boolean;
  isEventDetailPage?: boolean;
};

const Search: React.FC<Props> = (props) => {
  const { showToast } = useToast();
  const locale = useLocale();
  const [searchLoading, setSearchLoading] = useState(false);
  const [hotelOptions, setHotelOptions] = useState<Destination[]>([]);
  const [error, setError] = useState(false);
  const { selectedDate } = useDatePickerContext();
  const t = useTranslations();

  const {
    searchRef,
    calendarRef,
    destination,
    setDestination,
    isHomePage = true,
    isCustomClass,
    isEventDetailPage = false,
  } = props;

  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());

  const {
    travelersTotal,
    setTravelersTotal,
    travellerOptions,
    setTravallerOptions,
  } = useSetSearchParams({
    searchParams,
    router,
    isHomePage,
    destination,
    setDestination,
  });
  const { userDetails, getConnectedWallet } = useWalletService();

  useEffect(() => {
    // getHotelGroups();
    fetchPlaces();
  }, []);

  useEffect(() => {
    handleDateChange(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleFormSubmit = async (e: any) => {
    // handle form submit
    e.preventDefault();

    const isError = validateSearchRequest();

    if (!isError) {
      setSearchLoading(true);
      const hotelSearchRequest = createSearchRequest();

      if (userDetails?.walletId) {
        // private search
        await createHotelRequestToken(
          userDetails?.walletId?.trim(),
          hotelSearchRequest
        );
      } else {
        await searchForHotels(hotelSearchRequest);
      }
    }
  };

  const fetchPlaces = async () => {
    try {
      const res = await axios.get<{ places: Place[] }>(
        `/api/places?lang=${locale}`
      );

      const places = new Set();

      res?.data?.places.forEach((place) => {
        places.add({
          id: place?.id,
          destination: place?.fullName,
          hotelCount: 0,
          country: "",
          city: place?.name,
          label: place?.fullName,
          value: "0",
        });
      });
      setHotelOptions(res?.data?.places.slice(0, 5) || []);
    } catch (err) {
      console.log("Error fetching places: ", err);
      setHotelOptions([]);
    }
  };

  const validateSearchRequest = () => {
    if (selectedDate.length) {
      const isGreater = getDatesDiff(selectedDate, 30);

      if (isGreater) {
        setError(true);
        return true;
      }
    }

    return false;
  };

  const createSearchRequest = () => {
    const lastSearchRequest = getLastSearchRequestFromLocalStorage(locale);

    const geolocation: GeoLocation = {
      lat: undefined,
      lng: undefined,
    };

    const hotelSearchRequest = {
      id: destination?.id || lastSearchRequest?.id,
      from:
        selectedDate[0] && selectedDate[0].format(API_DATE_FORMAT).toString(),
      to: selectedDate[1] && selectedDate[1].format(API_DATE_FORMAT).toString(),
      numberOfChildren: Number(travellerOptions[1].value),
      numberOfAdults: Number(travellerOptions[0].value),
      numberOfInfants: Number(travellerOptions[2].value),
      city: destination?.city,
      country: destination?.country,
      currency: "USD",
      location: geolocation,
      locationName: undefined,
      destination: destination?.destination,
      wallet: userDetails?.walletId?.trim(),
    };

    return hotelSearchRequest;
  };

  const searchForHotels = async (hotelRequest: any) => {
    try {
      const body = {
        ...hotelRequest,
        partnerCode: getPartnerCode(),
      };
      setLastSearchRequestToLocalStorage(hotelRequest, locale);
      const { data } = await axios.post(`/api/search?lang=${locale}`, body);
      const filteredParams = getFilteredParams(searchParams);
      const title =
        !!hotelRequest?.destination && !isEmpty(hotelRequest.destination)
          ? `&title=${hotelRequest?.destination}`
          : "";
      router.replace(
        `/results?searchId=${data?.searchId}&${filteredParams.toString()}${title}`
      );
    } catch (err: any) {
      setSearchLoading(false);
      if (err?.response?.data?.error) {
        showToast(err.response.data.error, "error");
      } else if (err?.response?.statusText) {
        showToast(err.response.statusText, "error");
      } else {
        showToast(t("Other.errorOccurred"), "error");
      }
    }
  };

  const createHotelRequestToken = async (
    _walletAddress: string,
    hotelRequest: any
  ) => {
    try {
      if (!hotelRequest.country) {
        hotelRequest.country = undefined;
      }
      // const headers = {};
      const data = {
        ...hotelRequest,
        partnerCode: getPartnerCode(),
        connectedWallet: getConnectedWallet(),
      };

      const url = `/api/generateToken?lang=${locale}`;

      const res = await axios.post(url, data, { withCredentials: true });

      if (res?.data?.tokenId) {
        // redirect to the search result page &  save to local storage
        setLastSearchRequestToLocalStorage(hotelRequest, locale);
        const tokenId = res?.data?.tokenId;
        const filteredParams = getFilteredParams(searchParams);
        const title =
          !!hotelRequest?.destination && !isEmpty(hotelRequest.destination)
            ? `&title=${hotelRequest?.destination}`
            : "";
        router.replace(
          `/results?tokenId=${tokenId}&${filteredParams.toString()}${title}`
        );
      } else {
        setSearchLoading(false);
      }
    } catch (err) {
      setSearchLoading(false);
      console.error(err);
    }
  };

  const handleTravelOpClick = (op: string, label: string) => {
    const newOps = [...travellerOptions];

    const elem: OptionType | undefined = newOps.find(
      (o: any) => o.label === label
    );

    if (elem) {
      if (
        (op == "minus" && Number(elem?.value) > 0) ||
        (op == "plus" && travelersTotal < MAX_TRAVELER_COUNT)
      ) {
        elem.value =
          op == "minus"
            ? (Number(elem?.value) - 1).toString()
            : (Number(elem?.value) + 1).toString();
        setTravallerOptions(newOps);
        setTravelersTotal(
          op == "minus" ? travelersTotal - 1 : travelersTotal + 1
        );
      }
    }
  };

  const formatTravel = (props: SelectOption) => {
    const isDisabled =
      travelersTotal <= 0 ||
      props?.value === "0" ||
      (props?.label === t("Other.adults") && props?.value === "1");

    return (
      <div
        className="flex items-center justify-between border-b border-zinc-500 gap-2 capitalize sm:p-3 max-w-[1280px] last:border-0 text-sleap-gray-950 dark:text-white"
        id="let traveler of TRAVELER_TYPES"
      >
        <div className="flex flex-col">
          <div className="label font-semibold text-base font-switzer text-sleap-gray-950 dark:text-lightwhite leading-6">
            {props?.label}
          </div>
          <div className="font-medium text-sm text-sleap-gray-950 opacity-50">
            {/* @ts-ignore */}
            {t("HomePageHotels.yearsOld", { old: props.description })}
          </div>
        </div>
        <div className="flex gap-3 py-2 items-center">
          <button
            type="button"
            className="cursor-pointer sm:p-1 p-2 bg-sleap-purple-300 disabled:bg-sleap-gray-400 dark:bg-sleap-gray-600"
            disabled={isDisabled}
            onClick={() => handleTravelOpClick("minus", props?.label)}
          >
            <Minus
              className="w-5 h-5"
              color={isDisabled ? "#14141E" : "white"}
            />
          </button>
          <div className="text-2xl font-switzer font-semibold text-sleap-gray-950 dark:text-white">
            {" "}
            {props?.value}{" "}
          </div>
          <button
            type="button"
            className="cursor-pointer sm:p-1 p-2 bg-sleap-purple-300 disabled:bg-sleap-gray-400 dark:bg-sleap-gray-600"
            disabled={travelersTotal >= MAX_TRAVELER_COUNT}
            onClick={() => handleTravelOpClick("plus", props?.label)}
          >
            <Plus
              className="w-5 h-5"
              color={travelersTotal >= MAX_TRAVELER_COUNT ? "#14141E" : "white"}
            />
          </button>
        </div>
      </div>
    );
  };

  const travllerValueContainer = ({
    // eslint-disable-next-line no-unused-vars
    children,
    ...props
  }: SingleValueProps<any>) => {
    const totalTravelers: any = props?.options?.reduce((total, num) => {
      return { value: Number(total.value) + Number(num.value) };
    });

    return (
      <components.SingleValue {...props}>
        {totalTravelers?.value > 0
          ? totalTravelers?.value
          : t("Other.travellers")}
      </components.SingleValue>
    );
  };

  const handleDateChange = (date: [Dayjs | null, Dayjs | null]) => {
    if (date && date.length > 0) {
      setError(false);

      if (isHomePage) {
        params.set(
          SEARCH_PARAMS.CHECKIN,
          date[0]?.format(API_DATE_FORMAT) || ""
        );
        params.set(
          SEARCH_PARAMS.CHECKOUT,
          date[1]?.format(API_DATE_FORMAT) || ""
        );
        redirect(params);
      }
    }
  };

  const handleDestinationChange = (op: OptionType) => {
    const { city, destination, id } = op || {};

    if (destination) {
      params.set(SEARCH_PARAMS.CITY, city);
      params.set(SEARCH_PARAMS.DESTINATION, destination);
      params.set(SEARCH_PARAMS.ID, id);
    } else {
      params.delete(SEARCH_PARAMS.CITY);
      params.delete(SEARCH_PARAMS.DESTINATION);
      params.delete(SEARCH_PARAMS.ID, id);
    }

    redirect(params);
  };

  const handleTravellerChange = (_op: OptionType) => {
    params.set(SEARCH_PARAMS.ADULTS, travellerOptions[0].value);
    params.set(SEARCH_PARAMS.CHILDREN, travellerOptions[1].value);
    params.set(SEARCH_PARAMS.INFANTS, travellerOptions[2].value);

    redirect(params);
  };

  return (
    <form className="flex justify-center" onSubmit={handleFormSubmit}>
      <div
        id="search-box"
        ref={searchRef}
        className={`flex sm:p-3 py-4  w-full sm:m-auto sm:mx-0 flex-col gap-3 rounded-2xl  ${!isCustomClass ? "search-form p-2 xl:max-w-[1120px] xl:min-w-[1120px] max-w-[960px] bg-lightwhite dark:bg-sleap-gray-950 text-sleap-gray-950 dark:text-white sm:w-fit" : "!px-0 inner-pages-search w-full bg-sleap-gray-950 dark:bg-sleap-gray-950 text-white"}  `}
      >
        <div className="search-bar-container grid flex-col sm:grid-cols-2 grid-cols-1 md:grid gap-3 ">
          <CustomSelect
            placeHolder={t("HomePageHotels.whereDoYouWantToGo")}
            selectedValue={destination}
            setSelectedValue={setDestination}
            _options={hotelOptions}
            isSearchable={true}
            onOptionChange={handleDestinationChange}
            isCustomClass={isCustomClass}
            isEventDetailPage={isEventDetailPage}
          />
          <div ref={calendarRef}>
            <CustomDatePicker isCustomClass={isCustomClass} />
          </div>
          <Dropdown
            travellerOptions={travellerOptions}
            placeholder={t("Other.travellers")}
            className="md:w-full flex flex-row items-center rounded-lg gap-3 py-1 px-4 border-[1px] dark:border-input border-sleap-gray-400 bg-lightwhite dark:bg-sleap-gray-950 dark:hover:border-white h-14 text-center hover:border-sleap-gray-600 text-sleap-gray-800 dark:text-white "
            iconComp={
              <div className="w-5 h-5">
                <span className="dark:block hidden ">
                  <Users className="w-5 h-5" color={COLORS["white"]} />
                </span>
                <span className="dark:hidden block">
                  <Users
                    className="w-5 h-5"
                    color={COLORS[isCustomClass ? "white" : "black"]}
                  />
                </span>
              </div>
            }
            dropdownIndicator={true}
            options={travellerOptions}
            formatOptionLabel={formatTravel}
            isClearable={false}
            isSearchable={false}
            customComponents={{
              SingleValue: travllerValueContainer,
            }}
            selectedOption={{ value: travelersTotal }}
            closeMenuOnSelect={false}
            onClick={isHomePage ? handleTravellerChange : () => {}}
            isCustomClass={isCustomClass}
          />
          <div
            className={`relative ${!destination?.city || error ? "group" : ""}`}
          >
            <button
              className={`flex gap-2 w-full rounded-full ${isCustomClass ? "h-10" : "h-12"} font-semibold text-base text-white bg-sleap-purple-300 hover:bg-sleap-gray-950 font-switzer md:px-6 md:py-2`}
              disabled={searchLoading || error || !destination?.city}
            >
              {!searchLoading ? (
                <>
                  <SearchIcon className="w-5 h-5" color={COLORS["white"]} />
                  <span>
                    {isCustomClass && getDeviceType().isMobileType ? (
                      t("Other.updateSearch")
                    ) : isCustomClass ? (
                      <span className="lg:block hidden">
                        {t("Other.search")}
                      </span>
                    ) : (
                      t("Other.search")
                    )}
                  </span>
                </>
              ) : (
                <div className="w-full flex justify-center items-center h-6">
                  <span className="!h-9 !w-9 flex items-center justify-center">
                    <LoaderCircle className="text-white animate-spin" />
                  </span>
                </div>
              )}
            </button>
            <div className="absolute w-44 -bottom-10 lg:left-1/2 md:-right-12 right-0 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 flex flex-col items-center z-10">
              <div className="bg-gray-900 h-2 w-2 transform rotate-45"></div>
              <div className="bg-gray-900 text-white py-2 px-3 text-xs rounded-md shadow-lg -mt-1">
                {t("HomePageHotels.enterDestination")}
              </div>
            </div>
          </div>{" "}
        </div>
      </div>
    </form>
  );
};

export default Search;
